// The host's half of the keyboard's guarantees (plan § 4, Create: "What
// the keyboard guarantees, and where it drifts"). The keyboard commits only
// what its own rules allow; this is the backstop behind it: a word enters
// the composition only when the suggest route said the typed letters are a
// dictionary word or the child took a chip the route offered. It also names
// a dead end, the compact model's four-letter prefixes letting a child type
// into a string that begins no word, so the page can rescue in place.
import {
  type KeyboardModel,
  letterOptions,
  ONE_LETTER_WORDS,
  type SuggestResponse,
  SUGGEST_PREFIX_MIN_LENGTH,
  SUGGEST_TEXT_MAX_LENGTH,
} from "@bindery/dynamic-keyboard";
import type { CompositionToken } from "@bindery/composition";
import { storyText } from "./ladder";

export interface Remembered {
  readonly prefix: string;
  readonly response: SuggestResponse;
}

/** Answers the route gave for this word so far, oldest first. */
export type SuggestMemory = readonly Remembered[];

const MEMORY_CAP = 32;

export const remember = (
  memory: SuggestMemory,
  prefix: string,
  response: SuggestResponse,
): SuggestMemory =>
  [...memory.filter((m) => m.prefix !== prefix), { prefix, response }].slice(
    -MEMORY_CAP,
  );

export const forget = (): SuggestMemory => [];

export type Admission = "word" | "chip" | "refused";

/**
 * Whether a word the keyboard completed may enter the composition, and why.
 * The route is never asked about one letter, so the one-letter words are
 * admitted by the same list the keyboard finishes them from.
 */
export function admit(memory: SuggestMemory, word: string): Admission {
  const key = word.toLowerCase();
  if (ONE_LETTER_WORDS.has(key)) return "word";
  if (memory.some((m) => m.prefix === key && m.response.isWord)) return "word";
  if (memory.some((m) => m.response.suggestion?.toLowerCase() === key))
    return "chip";
  return "refused";
}

/** The word as it enters the story: the keyboard types lower case, and "i" is written "I". */
export const asWritten = (word: string): string =>
  word.toLowerCase() === "i" ? "I" : word;

/** The newest chip the route offered for any prefix of what was typed. */
export const bestChip = (memory: SuggestMemory, typed: string): string | null =>
  [...memory]
    .reverse()
    .find((m) => m.response.suggestion !== null && typed.startsWith(m.prefix))
    ?.response.suggestion ?? null;

export interface DeadEnd {
  readonly prefix: string;
  readonly chip: string | null;
}

/**
 * The typed letters have left the word list: the route knows no word that
 * starts with them and they are not one, or the model offers no next letter
 * and they are not one. Null while the child is still on a path to a word,
 * and null until the route has answered for `typed` itself: an answer for
 * an earlier prefix, however recent, says nothing about the letters now on
 * the keyboard.
 */
export function deadEnd(
  model: KeyboardModel,
  memory: SuggestMemory,
  typed: string | null,
): DeadEnd | null {
  const current = memory.find((m) => m.prefix === typed);
  if (current === undefined) return null;
  const { prefix, response } = current;
  if (response.isWord || prefix.length < SUGGEST_PREFIX_MIN_LENGTH) return null;
  const noWordAhead = response.suggestion === null;
  const noLetterAhead = letterOptions(model, prefix, 4).primary.length === 0;
  return noWordAhead || noLetterAhead
    ? { prefix, chip: bestChip(memory, prefix) }
    : null;
}

/** The text before the insertion point, as the suggest route's `text`. */
export const keyboardContext = (
  tokens: readonly CompositionToken[],
  atIndex: number,
): string =>
  storyText(tokens.slice(0, atIndex)).slice(-SUGGEST_TEXT_MAX_LENGTH);
